import { useTradingSettings } from '@src/providers/AppConfigProvider';
import {
  type FilterableProperty,
  filterByCellValueMenuItem,
  getShowJSONContextItem,
  type Leaves,
  useCallbackRef,
  type UseFilterBuilderOutput,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, IRowNode, MenuItemDef } from 'ag-grid-community';
import { compact, isNil } from 'lodash-es';
import { type ReactNode, useMemo } from 'react';
import { useForceCancelCareOrderChildMenuItem } from '../CareOrders/useForceCancelCareOrderChildMenuItem';
import { getCopyFundQuotePricesItem } from '../Quotes/MenuItems/copyFundQuotePricesItem';
import { useShowRFQMenuItem } from '../Quotes/MenuItems/useShowRFQMenuItem';
import { useShowTakerQuoteBreakdownMenuItem } from '../Quotes/MenuItems/useShowTakerQuoteBreakdownMenuItem';
import { QuoteRow, type QuotesBlotterRow } from './types';
import type { QuotesV2TableFilter } from './useQuotesV2Filter';

export function useQuotesV2Menu({
  openClause,
  filterableProperties,
}: {
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties: FilterableProperty<string>[];
}): {
  getContextMenuItems: (params: GetContextMenuItemsParams<QuotesBlotterRow>) => (MenuItemDef | string)[];
  dialogs: ReactNode;
} {
  const mixpanel = useMixpanel();
  const { handleClickJson, jsonModal } = useJsonModal();

  const { getForceCancelCareOrderChildQuoteItem, dialog: forceCancelDialog } = useForceCancelCareOrderChildMenuItem();
  const getShowRFQMenuItem = useShowRFQMenuItem();
  const getShowTakerQuoteBreakdownMenuItem = useShowTakerQuoteBreakdownMenuItem();
  const { rfqBestQuoteBy } = useTradingSettings();

  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<QuotesBlotterRow>) => {
    const row = params?.node?.data;
    if (isNil(row)) {
      return [];
    }

    const quote = row instanceof QuoteRow ? row.quote : undefined;
    const copyPricingItem = getCopyFundQuotePricesItem(getSelectedQuotesWithParentCOs(params), rfqBestQuoteBy);
    let menuItems: MenuItemDef[] = [];
    if (filterableProperties != null && openClause != null) {
      menuItems = menuItems.concat(
        filterByCellValueMenuItem({
          params,
          filterableProperties,
          openClause,
          colIDToFilterBuilderKey,
          mixpanel,
        })
      );
    }

    return compact([
      ...menuItems,
      copyPricingItem,
      ...(quote
        ? [
            getShowRFQMenuItem(quote),
            getShowTakerQuoteBreakdownMenuItem(quote),
            getForceCancelCareOrderChildQuoteItem(quote),
          ]
        : []),
      'separator',
      getShowJSONContextItem({ params, handleClickJson }),
      'separator',
      ...getDefaultContextMenuItems(params),
    ]);
  });

  const dialogs = useMemo(
    () => (
      <>
        {jsonModal}
        {forceCancelDialog}
      </>
    ),
    [jsonModal, forceCancelDialog]
  );

  return {
    getContextMenuItems,
    dialogs,
  };
}

function getSelectedQuotesWithParentCOs(
  params: GetContextMenuItemsParams<QuotesBlotterRow>
): Parameters<typeof getCopyFundQuotePricesItem>[0] {
  const selectedQuoteNodes = params.api
    .getSelectedNodes()
    .filter((node): node is IRowNode<QuoteRow> => node.data instanceof QuoteRow);

  return selectedQuoteNodes.map(node => ({
    quote: node.data?.quote,
    parentCO: node.data?.quote?.careOrder,
  }));
}

export function colIDToFilterBuilderKey(id: string): keyof QuotesV2TableFilter | undefined {
  switch (id as Leaves<QuotesBlotterRow>) {
    case 'Symbol':
      return 'Symbols';
    case 'quote.subAccounts':
      return 'SubAccounts';
    case 'quote.tradedMarkets':
      return 'Markets';
    case 'Side':
      return 'Sides';
    case 'quote.TradedSide':
      return 'TradedSides';
    case 'QuoteStatus':
      return '_statuses';
    case 'quote.User':
      return 'Users';
    case 'quote.RFQID':
      return 'RFQID';
    case 'quote.ParentRFQID':
      return 'ParentRFQID';
  }
  return undefined;
}
